import React, { useState, useEffect, useContext, useCallback } from "react";
import Axios from "axios";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { toast } from "react-toastify";
import { API_ROUTE, Routes } from "../../Helpers/Constants";
import AuthenticationContext from "../../Store/Authentication-Context";
import { NavBar } from "../NavBar";
import KnownErrorDetailsModal from "../MainComponents/KnownErrorDetailsModal";
import SuppressErrorPopup from "../MainComponents/SuppressErrorPopup";
import ConfirmSuppressionRemovalModal from "../MainComponents/ConfirmSuppressionRemovalModal";
import { knownErrorColumns, suppressionGridProps } from "../MainComponents/SuppressionColumns";
import * as SuppressionApi from "../../Helpers/SuppressionApi";
import ActingOnBehalfBanner from "../MainComponents/ActingOnBehalfBanner";
import { currentManagedOrg, currentSuppressionPageUrl } from "../../Helpers/SuppressionOrg";

/**
 * Task 9 — Error Suppression: the known error catalog with this organization's suppression state.
 *
 * Note this is a CUSTOMER page. It gates on session validity the way Main.js does, and must not
 * copy the ResellerID === "1" redirect used by Pages/KnownErrors.js, which is the master-admin
 * catalog editor and a different feature entirely.
 */
export default function ErrorSuppression() {
    const authContext = useContext(AuthenticationContext);
    const { setAuthContext } = authContext;

    const [rowData, setRowData] = useState([]);
    const [accessLevel, setAccessLevel] = useState("None");
    const [loading, setLoading] = useState(true);
    const [jobs, setJobs] = useState([]);

    const [search, setSearch] = useState("");
    const [filterSeverity, setFilterSeverity] = useState("");

    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [showDetail, setShowDetail] = useState(false);

    const [suppressTarget, setSuppressTarget] = useState(null);
    const [confirm, setConfirm] = useState(null);
    const [confirmBusy, setConfirmBusy] = useState(false);

    const canChange = accessLevel === "Change";

    // Customer pages confirm the session is still valid rather than checking a role — see
    // Pages/Main.js. The per-role gate is enforced server-side by the front-end proxy's
    // SuppressionAccess filter; this page only reflects the access level the API reports back, which
    // is why no role is inspected here.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const token = await setAuthContext(new Date());
                // Same resolution the API calls use, so the session check and the data are scoped
                // to one organisation. Reading sessionStorage alone validated the wrong one on a
                // pasted ?orgId= link, where the URL is the only correct source.
                const { orgId } = currentManagedOrg();
                const config = {
                    headers: {
                        Authorisation: `Bearer ${token}`,
                        CloudCoverToken: sessionStorage.getItem("CloudCoverToken"),
                    },
                };
                const response = await Axios.get(
                    `${API_ROUTE}${Routes.VALIDATE_TOKEN}/${orgId}`,
                    config
                );
                if (!cancelled && !JSON.parse(response.data)) window.location.href = "/login";
            } catch (err) {
                if (err?.response?.status === 440) window.location.href = "/TimeoutErrorHandler";
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [setAuthContext]);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const result = await SuppressionApi.getKnownErrors(setAuthContext, {
                search: search || null,
                severity: filterSeverity || null,
            });
            setRowData(result.items || []);
            setAccessLevel(result.accessLevel || "None");
        } catch (err) {
            const message = SuppressionApi.handleApiError(err, "Failed to load known errors.");
            if (message) toast.error(message);
        } finally {
            setLoading(false);
        }
    }, [search, filterSeverity, setAuthContext]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    useEffect(() => {
        (async () => {
            try {
                setJobs(await SuppressionApi.getJobs(setAuthContext));
            } catch (err) {
                const message = SuppressionApi.handleApiError(err, "Failed to load your jobs.");
                if (message) toast.error(message);
            }
        })();
    }, [setAuthContext]);

    async function openDetails(row) {
        setShowDetail(true);
        setDetailLoading(true);
        setDetail(null);
        try {
            setDetail(await SuppressionApi.getKnownErrorDetail(setAuthContext, row.id));
        } catch (err) {
            const message = SuppressionApi.handleApiError(err, "Failed to load error details.");
            if (message) toast.error(message);
        } finally {
            setDetailLoading(false);
        }
    }

    async function confirmSuppression({ jobIds, suppressedUntil, note }) {
        try {
            await SuppressionApi.createSuppressions(setAuthContext, {
                knownErrorId: suppressTarget.id,
                // C1 — one duration for every selected job.
                jobIds,
                suppressedUntil,
                // C5 — the optional justification, already length-checked in the duration step.
                note,
            });
            toast.success(
                `${suppressTarget.errorId} suppressed on ${jobIds.length} job${
                    jobIds.length === 1 ? "" : "s"
                }.`
            );
            setSuppressTarget(null);
            loadData();
        } catch (err) {
            const message = SuppressionApi.handleApiError(err, "Failed to suppress this error.");
            if (message) toast.error(message);
        }
    }

    async function runConfirmedRemoval() {
        setConfirmBusy(true);
        try {
            await confirm.action();
            toast.success("Suppression removed.");
            setConfirm(null);
            loadData();
            // Keep an open details modal in step with the change.
            if (showDetail && detail) {
                setDetail(await SuppressionApi.getKnownErrorDetail(setAuthContext, detail.id));
            }
        } catch (err) {
            const message = SuppressionApi.handleApiError(err, "Failed to remove the suppression.");
            if (message) toast.error(message);
        } finally {
            setConfirmBusy(false);
        }
    }

    const columnDefs = knownErrorColumns({
        canChange,
        onViewDetails: openDetails,
        onSuppress: (row) => setSuppressTarget(row),
        // D2 — the count matters: "all of your jobs" reads very differently when it is 40 of them.
        onRemoveAll: (row) =>
            setConfirm({
                title: "Remove suppression",
                message: `Remove the suppression for ${row.errorId} on all ${row.suppressedJobCount} of your jobs? You will start being notified about this error again.`,
                action: () => SuppressionApi.removeAllSuppressionsForError(setAuthContext, row.id),
            }),
    });

    return (
        <div>
            <NavBar page="errorSuppression" authContext={authContext} />
            <div style={{ padding: 20 }}>
                <ActingOnBehalfBanner />
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">Error Suppression</h5>
                    {/* btn-primary to match the "Add Tenant" button on MasterAdmin
                        (MasterAdmin.js:4197). general.css:988 themes .btn-primary to
                        var(--button-back) with var(--button-text), so this picks up the brand
                        colour rather than Bootstrap's default blue.

                        The organisation MUST travel with this link. Once the managed organisation
                        scopes the data, a bare href drops it on a pasted ?orgId= link and silently
                        returns the operator to their own organisation. */}
                    <a className="btn btn-primary" href={currentSuppressionPageUrl("/ManageSuppressions")}>
                        Manage Suppressions
                    </a>
                </div>

                <p className="text-muted" style={{ maxWidth: 820 }}>
                    Suppress known errors that you have already assessed and accepted. Suppressed
                    errors are still recorded against your backup jobs — they simply stop triggering
                    notifications, and a job whose issues are all suppressed is reported as
                    successful.
                </p>

                <div className="d-flex gap-2 mb-3 flex-wrap">
                    <input
                        type="text"
                        className="form-control"
                        style={{ maxWidth: 280 }}
                        placeholder="Search name, error ID, description…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    <select
                        className="form-select"
                        style={{ maxWidth: 170 }}
                        value={filterSeverity}
                        onChange={(e) => setFilterSeverity(e.target.value)}
                    >
                        {/*
                          B3 — Error and Warning only. Info-severity entries never reach this list
                          because only Error and Warning affect a job's status, so offering Info here
                          would be a filter that can only ever return nothing.
                        */}
                        <option value="">All severities</option>
                        <option value="Error">Error</option>
                        <option value="Warning">Warning</option>
                    </select>
                </div>

                {loading ? (
                    <div className="alert alert-light border text-center py-5 mb-0">Loading…</div>
                ) : rowData.length === 0 ? (
                    <div className="alert alert-light border text-center py-5 mb-0">
                        <p className="mb-1 fw-semibold">No known errors found.</p>
                        <p className="mb-0 text-muted" style={{ fontSize: 14 }}>
                            Try clearing the search or severity filter.
                        </p>
                    </div>
                ) : (
                    <div className="ag-theme-alpine">
                        <AgGridReact {...suppressionGridProps} rowData={rowData} columnDefs={columnDefs} />
                    </div>
                )}
            </div>

            <KnownErrorDetailsModal
                show={showDetail}
                detail={detail}
                loading={detailLoading}
                canChange={canChange}
                onClose={() => setShowDetail(false)}
                onRemoveJobSuppression={(job) =>
                    setConfirm({
                        title: "Remove suppression",
                        message: `Remove the suppression for ${detail?.errorId} on "${job.jobName}"? You will start being notified about this error on that job again.`,
                        action: () =>
                            SuppressionApi.removeSuppression(setAuthContext, job.suppressionId),
                    })
                }
            />

            <SuppressErrorPopup
                show={!!suppressTarget}
                knownError={suppressTarget}
                jobs={jobs}
                // C4 — comes with the list row, so the overwrite prompt costs no extra request.
                alreadySuppressed={suppressTarget ? suppressTarget.suppressedJobs : []}
                onConfirm={confirmSuppression}
                onClose={() => setSuppressTarget(null)}
            />

            <ConfirmSuppressionRemovalModal
                show={!!confirm}
                title={confirm?.title}
                message={confirm?.message}
                busy={confirmBusy}
                onConfirm={runConfirmedRemoval}
                onClose={() => setConfirm(null)}
            />
        </div>
    );
}
